import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  message,
  Space,
  Alert
} from 'antd';
import {
  UserOutlined,
  MailOutlined,
  LockOutlined,
  TeamOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone
} from '@ant-design/icons';
import {
  createUser,
  clearError,
  clearSuccess
} from '../../features/account/userManagementSlice';

const AddUser = ({ visible, onCancel, onSuccess }) => {
  const dispatch = useDispatch();
  const { loading, error, success, operationType } = useSelector(
    (state) => state.userManagement
  );
  
  const [form] = Form.useForm();

  useEffect(() => {
    if (success && operationType === 'create') {
      message.success('User created successfully!');
      dispatch(clearSuccess());
      form.resetFields();
      onSuccess && onSuccess();
    }
  }, [success, operationType, dispatch, form, onSuccess]);

  useEffect(() => {
    if (error && operationType === 'create') {
      message.error(error);
      dispatch(clearError());
    }
  }, [error, operationType, dispatch]);

  const handleSubmit = async (values) => {
    try {
      await dispatch(createUser(values)).unwrap();
    } catch (error) {
      // Error is handled by useEffect
    }
  };

  const handleCancel = () => {
    form.resetFields();
    dispatch(clearError());
    onCancel && onCancel();
  };

  const roleOptions = [
    {
      value: 'admin',
      label: (
        <div className="flex items-center space-x-2">
          <TeamOutlined />
          <span>Admin - Full system access</span>
        </div>
      )
    },
    {
      value: 'omega',
      label: (
        <div className="flex items-center space-x-2 text-red-600">
          <TeamOutlined />
          <span>Omega - Access to Creond Desktop 🔒</span>
        </div>
      )
    },
    {
      value: 'devuser',
      label: (
        <div className="flex items-center space-x-2">
          <UserOutlined />
          <span>Developer User - Development & Media access</span>
        </div>
      )
    },
    {
      value: 'desuser',
      label: (
        <div className="flex items-center space-x-2">
          <UserOutlined />
          <span>Designer User - Design & Media access</span>
        </div>
      )
    },
    {
      value: 'prouser',
      label: (
        <div className="flex items-center space-x-2">
          <UserOutlined />
          <span>Pro User - All features except Account</span>
        </div>
      )
    },
    {
      value: 'visitor',
      label: (
        <div className="flex items-center space-x-2">
          <UserOutlined />
          <span>Visitor - Basic access only</span>
        </div>
      )
    }
  ];

  return (
    <Modal
      title={
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-blue-600 rounded-full flex items-center justify-center">
            <UserOutlined className="text-white text-sm" />
          </div>
          <span>Add New User</span>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
      destroyOnClose
    >
      <div className="mb-4">
        <Alert
          message="Create New User Account"
          description="Fill in the user details and assign an appropriate role. The user will receive login credentials and can change their password after first login."
          type="info"
          showIcon
        />
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        size="large"
        className="mt-4"
      >
        <Form.Item
          label="Full Name"
          name="name"
          rules={[
            { required: true, message: 'Please enter the user\'s full name' },
            { min: 2, message: 'Name must be at least 2 characters long' },
            { max: 50, message: 'Name cannot exceed 50 characters' }
          ]}
        >
          <Input
            prefix={<UserOutlined className="text-gray-400" />}
            placeholder="Enter user's full name"
            className="rounded-lg"
          />
        </Form.Item>

        <Form.Item
          label="Email Address"
          name="email"
          rules={[
            { required: true, message: 'Please enter the user\'s email' },
            { type: 'email', message: 'Please enter a valid email address' }
          ]}
        >
          <Input
            prefix={<MailOutlined className="text-gray-400" />}
            placeholder="Enter user's email address"
            className="rounded-lg"
          />
        </Form.Item>

        <Form.Item
          label="Password"
          name="password"
          rules={[
            { required: true, message: 'Please enter a password' },
            { min: 6, message: 'Password must be at least 6 characters long' },
            { 
              pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
              message: 'Password must contain at least one uppercase letter, one lowercase letter, and one number'
            }
          ]}
          extra="Password should be at least 6 characters with uppercase, lowercase, and number"
        >
          <Input.Password
            prefix={<LockOutlined className="text-gray-400" />}
            placeholder="Enter a secure password"
            className="rounded-lg"
            iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
          />
        </Form.Item>

        <Form.Item
          label="User Role"
          name="role"
          rules={[
            { required: true, message: 'Please select a user role' }
          ]}
          extra="Choose the appropriate role based on the user's responsibilities"
        >
          <Select
            placeholder="Select user role"
            className="rounded-lg"
            options={roleOptions}
            optionRender={(option) => option.label}
          />
        </Form.Item>

        <Form.Item className="mb-0 pt-4">
          <Space className="w-full justify-end">
            <Button 
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<UserOutlined />}
              className="bg-green-600 hover:bg-green-700 border-green-600 hover:border-green-700"
            >
              {loading ? 'Creating User...' : 'Create User'}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddUser;